import { Icon } from 'Components';
import { Select, Table, Spin, Checkbox } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { FILTER_STATE, SelectOption, TABLE_STATE, UserData } from '../CampaignReport.type';
import UserDetailDrawer from './UserDetailDrawer';
import { useIsMobile } from 'Hooks';
import PaginationElement from 'PhishingManager/components/Pagination';
import { INDIVIDUAL_TABLE_COLUMNS } from '../CampaignReport.constant';
import { ColumnsType } from 'antd/es/table';
import IndividualListItem from './IndividualListItem';
import DebounceInput from 'PhishingManager/components/DebouncedInput';
import InfinityScrollWrapper from 'Components/InfinityScroll/InfinityScrollWrapper';
import { useTranslation } from 'react-i18next';
import { getIndividualUserData } from 'PhishingManager/service/index.Service';

// Performed By Options
const performedByOptions: SelectOption[] = [
    { label: "phishingManager.report.table.filters.performedBy.allSources", value: "" },
    { label: "phishingManager.report.table.filters.performedBy.human", value: "Human" },
    { label: "phishingManager.report.table.filters.performedBy.system", value: "System" },
    { label: "N/A", value: "N/A" },
];

// Result Options
const resultOptions: SelectOption[] = [
    { label: "phishingManager.report.table.filters.result.allResults", value: "" },
    { label: "phishingManager.report.table.filters.result.passed", value: "Passed" },
    { label: "phishingManager.report.table.filters.result.failed", value: "Failed" }
];

// Interaction Options
const interactionOptions: SelectOption[] = [
    { label: "phishingManager.report.table.filters.interaction.allInteractions", value: "" },
    { label: "phishingManager.report.table.filters.interaction.noInteraction", value: "No Interaction" },
    { label: "phishingManager.report.table.filters.interaction.emailSent", value: "Email Sent" },
    { label: "phishingManager.report.table.filters.interaction.emailOpened", value: "Email Opened" },
    { label: "phishingManager.report.table.filters.interaction.clickedLink", value: "Clicked Link" },
    { label: "phishingManager.report.table.filters.interaction.submittedData", value: "Submitted Data" }
];

// Delivery Status Options
const DELIVERY_STATUS: SelectOption[] = [
    { label: "phishingManager.report.table.filters.deliveryStatus.all", value: "" },
    { label: "phishingManager.report.table.filters.deliveryStatus.delivered", value: "Yes" },
    { label: "phishingManager.report.table.filters.deliveryStatus.undelivered", value: 'No' }
];

// Confidence Multi-Select Options
const CONFIDENCE_OPTIONS = [
    { label: "High", value: "high" },
    { label: "Medium", value: "medium" },
    { label: "Low", value: "low" },
    { label: "N/A", value: "N/A" }
];

const ALL_CONFIDENCE_VALUES = ["high", "medium", "low", "N/A"];

const initDeliveredState: FILTER_STATE = {
    search: "",
    property: "",
    group: "",
    doneBy: "",
    result: "",
    deliveryStatus: "Yes",
    interaction: "",
    confidence: ALL_CONFIDENCE_VALUES as any,
    event_date: "",
    page: 1,
    loading: false
};

const PAGE_SIZE = 10;

const IndividualTab = ({ tableState, venzaTenentId, campaignId }: { tableState: TABLE_STATE, venzaTenentId: number, campaignId: number | null }) => {
    const [individualData, setIndividualData] = useState<{ data: UserData[], total: number, tableKey: number }>({ data: [], total: 0, tableKey: 0 });
    const [individualState, setIndividualState] = useState<FILTER_STATE>({ ...initDeliveredState });
    const [selectedData, setSelectedData] = useState<UserData | null>(null);
    const isMobile = useIsMobile(1191);
    const { t } = useTranslation();
    const { search, property, group, doneBy, interaction, result, deliveryStatus, confidence, event_date, page, loading } = individualState;

    const tableData: UserData[] = individualData.data;
    const tableColumn: ColumnsType<any> = INDIVIDUAL_TABLE_COLUMNS(t);

    const { propertyOptions, groupOptions }: { propertyOptions: SelectOption[], groupOptions: SelectOption[] } = useMemo(() => {
        const filterAllPath = `phishingManager.report.table.filters.all`;
        return {
            propertyOptions: [{ label: t(`${filterAllPath}properties`), value: "" }, ...(tableState.properties.map((item) => ({
                label: item,
                value: item
            })))],
            groupOptions: [{ label: t(`${filterAllPath}groups`), value: "" }, ...(tableState.groups.map((item) => ({
                label: item,
                value: item
            })))],
        };
    }, [tableState.properties, tableState.groups, t]);

    const handleState = (selectKey: string, value: any) => {
        setIndividualState(prev => ({ ...prev, [selectKey]: value, page: 1 }));
    };

    const setCurrentPage = (page: number) => {
        setIndividualState(prev => ({ ...prev, page }));
    };

    const getIndividualData = async () => {
        try {
            setIndividualState(prev => ({ ...prev, loading: true }));
            
            const formattedConfidence = Array.isArray(confidence) ? confidence.join(",") : confidence;

            const queryParams = {
                ...individualState,
                confidence: formattedConfidence
            };

            const response = await getIndividualUserData(venzaTenentId, campaignId || 0, queryParams as any);
            setIndividualData(prev => {
                const newData = response?.data?.results || [];
                return { 
                    data: isMobile ? [...prev.data, ...newData] : newData, 
                    total: response?.meta?.total || 0,
                    tableKey: Math.random() * PAGE_SIZE
                };
            });
        } catch (error) {
            setIndividualData({ data: [], total: 0, tableKey: 0 });
        } finally {
            setIndividualState(prev => ({ ...prev, loading: false }));
        }
    };

    useEffect(() => {
        if (!venzaTenentId || !campaignId) setIndividualData({ data: [], total: 0, tableKey: 0 });
        else getIndividualData();
    }, [campaignId, page, search, property, group, result, doneBy, interaction, deliveryStatus, confidence, event_date]);

    useEffect(() => {
        if (tableState.isPropertyLoading) setIndividualState(prev => ({ ...initDeliveredState, loading: prev.loading }));
    }, [tableState.isPropertyLoading]);

    const handleConfidenceChange = (checkedValue: string) => {
        let currentValues: string[] = Array.isArray(confidence) ? [...confidence] : [];
        if (currentValues.includes(checkedValue)) {
            currentValues = currentValues.filter((v) => v !== checkedValue);
        } else {
            currentValues.push(checkedValue);
        }
        handleState("confidence", currentValues);
    };

    const selectedConfidenceList: string[] = Array.isArray(confidence) ? (confidence as any) : [];
    const confidenceDisplayTitle = selectedConfidenceList.length === CONFIDENCE_OPTIONS.length || selectedConfidenceList.length === 0
        ? "All Confidences"
        : `${selectedConfidenceList.length} Selected`;

    return (
        <Spin spinning={loading}>
            <div className="report-section">
                {/* <div className="table-header">
                    <div className='description'>{t(`phishingManager.report.table.header.individual`)}</div>
                </div> */}

                <div className={`campaign-filter-section ${isMobile ? 'd-flex flex-column gap-12 w-full' : 'd-flex flex-column align-start gap-12 w-full'}`}>
                    
                    <div className={isMobile ? 'w-full' : 'w-full d-flex align-center'}>
                        <DebounceInput 
                            debounceSearch={search} 
                            placeholder={t('phishingManager.campaignSetup.step1.search')} 
                            setDebouncedSearch={(value) => handleState("search", value)} 
                            className="campaign-setup-search" 
                        />
                    </div>

                    <div className={isMobile ? 'd-flex flex-column gap-8 w-full' : 'w-full d-flex align-center gap-8 flex-wrap'}>
                        <SelectElement className={isMobile ? 'w-full' : ''} value={property} options={propertyOptions} onChange={(value) => handleState("property", value)} />
                        <SelectElement className={isMobile ? 'w-full' : ''} value={group} options={groupOptions} onChange={(value) => handleState("group", value)} />
                        <SelectElement className={isMobile ? 'w-full' : ''} value={result} options={resultOptions.map((item) => ({ ...item, label: t(item.label) }))} onChange={(value) => handleState("result", value)} />
                        <SelectElement className={isMobile ? 'w-full' : ''} value={doneBy} options={performedByOptions.map((item) => ({ ...item, label: t(item.label) }))} onChange={(value) => handleState("doneBy", value)} />
                        <SelectElement className={isMobile ? 'w-full' : ''} value={interaction} options={interactionOptions.map((item) => ({ ...item, label: t(item.label) }))} onChange={(value) => handleState("interaction", value)} />

                        <Select
                            className={`campaign-setup-select confidence-custom-select ${isMobile ? 'w-full' : ''}`}
                            dropdownClassName="campaign-setup-filter-option-select confidence-dropdown-overlay"
                            suffixIcon={
                                <div className="cs-dropdownWrapper">
                                    <Icon type="campaignSetupDropdown" />
                                </div>
                            }
                            value={confidenceDisplayTitle}
                            getPopupContainer={(trigger) => trigger.parentElement}
                            dropdownRender={() => (
                                <div className="confidence-checkbox-menu" style={{ padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {CONFIDENCE_OPTIONS.map((opt) => (
                                        <Checkbox 
                                            key={opt.value}
                                            checked={selectedConfidenceList.includes(opt.value)}
                                            onChange={() => handleConfidenceChange(opt.value)}
                                            style={{ margin: 0, fontWeight: 500 }}
                                        >
                                            {opt.label}
                                        </Checkbox>
                                    ))}
                                </div>
                            )}
                        />

                        <SelectElement className={isMobile ? 'w-full' : ''} value={deliveryStatus} options={DELIVERY_STATUS.map((item) => ({ ...item, label: t(item.label) }))} onChange={(value) => handleState("deliveryStatus", value)} />
                    </div>
                </div>

                {(!isMobile || !tableData.length) ? (
                    <div className='table-section'>
                        <Table
                            key={individualData.tableKey}
                            rowKey={(record) => record.key || record.id}
                            className="report-table"
                            dataSource={tableData}
                            showHeader={tableData.length > 0}
                            columns={tableColumn}
                            pagination={false}
                            showSorterTooltip={false}
                            rowClassName={(_, index) => `${index % 2 === 0 ? "bg-white" : "bg-gray"} cursor-pointer`}
                            onRow={(record) => {
                                return {
                                    onClick: () => setSelectedData(record),
                                };
                            }}
                        />
                        <PaginationElement
                            total={individualData.total}
                            pageSize={PAGE_SIZE}
                            current={page}
                            onChange={setCurrentPage}
                        />
                    </div>
                ) : (
                    <div className='list-section'>
                        <InfinityScrollWrapper
                            dataLength={tableData.length}
                            hasMore={tableData.length < individualData.total}
                            onLoadMore={() => setTimeout(() => setCurrentPage(page + 1), 500)}
                            height={600}
                            endMessageText={t('phishingManager.campaignSetup.step3.property.allItemLoaded')}
                            scrollableId="user-data-scroll-container">
                            {tableData.map((item, idx) => (
                                <IndividualListItem t={t} userData={item as UserData} key={idx} />
                            ))}
                        </InfinityScrollWrapper>
                    </div>
                )}
            </div>
            {!!selectedData && <UserDetailDrawer data={selectedData} visible={!!selectedData} onClose={() => setSelectedData(null)} />}
        </Spin>
    );
};

export default IndividualTab;

const SelectElement = ({ value, options, onChange, className }: { value: number | string, options: SelectOption[], onChange: (value: any) => void, className?: string }) => {
    return (
        <Select
            className={`campaign-setup-select ${className || ''}`}
            dropdownClassName="campaign-setup-filter-option-select"
            suffixIcon={
                <div className="cs-dropdownWrapper">
                    <Icon type="campaignSetupDropdown" />
                </div>
            }
            disabled={options.length <= 1}
            value={value}
            onChange={onChange}
            options={options}
            getPopupContainer={(trigger) => trigger.parentElement}
        />
    );
};